import { useEffect, useState } from 'react';
import {
  DevHubAlert,
  DevHubBox,
  DevHubColorProps,
  DevHubGrid,
} from '@devxdiscover/devhub-ui';
import { IncidentOverview, useIOPIncidentRequest } from '../../services/iop';
import {
  GitHubPr,
  PrStatusType,
  useGitHubPrRequest,
} from '../../services/github';
import { SecurityVulnerabilities } from './SecurityVulnerabilities';
import { ConfigFeatureCheck } from '../../util/ConfigFeatureCheck';
import { WorkBoxItem } from './WorkboxItem';
import { Feature } from '@devxdiscover/devhub-common';
import { Progress } from '@backstage/core-components';

/**
 * The properties for the {@link MySummary} component.
 */
export type MySummaryProps = {
  expanded?: boolean;
  incidentOverview: IncidentOverview;
  pullRequests: GitHubPr[];
  errors: {
    incidentsHasError: boolean;
    prsError: boolean;
  };
  loading: {
    incidentsIsLoading: boolean;
    prsLoading: boolean;
  };
};

const SummaryTable = ({
  incidentOverview,
  pullRequests,
  errors,
  loading,
}: MySummaryProps) => {
  const prStatusColors = new Map<PrStatusType, DevHubColorProps>([
    [PrStatusType.PendingMerge, 'blue'],
    [PrStatusType.PendingReview, 'green'],
    [PrStatusType.PendingApproval, 'purple'],
  ]);

  const [secVulnsShowMore, setSecVulnsShowMore] = useState(false);
  const [incidentReport, setIncidentReport] = useState<IncidentOverview>({
    numApps: 0,
    incidents: [],
    summary: [],
  });

  useEffect(() => {
    if (incidentOverview !== undefined) {
      setIncidentReport(incidentOverview);
    }
  }, [incidentOverview]);

  const severityColors: { [key: string]: DevHubColorProps } = {
    5: 'green',
    4: 'purple',
    3: 'blue',
    2: 'yellow',
    1: 'red',
  };

  function formatDate(date: string | number | Date) {
    if (date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    }
    return '';
  }

  return (
    <DevHubBox>
      {!loading && !errors && (
        <DevHubAlert severity="info">No jira tickets found.</DevHubAlert>
      )}
      {!loading.incidentsIsLoading && (
        <DevHubGrid container spacing={2}>
          {incidentReport?.incidents?.slice(0, 6).map(object => (
            <DevHubGrid item xs={12} lg={4}>
              <WorkBoxItem
                key={object?.sysId ? object?.sysId : object?.message}
                chipLabel={object?.severity.toString()}
                chipColor={severityColors[object?.severity]}
                title={`${object?.incidentNumber} - ${object?.appName}`}
                date={formatDate(object?.startTime)}
                link={`https://comcast.service-now.com/incident.do?sys_id=${object.sysId}`}
                body={object.message}
              />
            </DevHubGrid>
          ))}
        </DevHubGrid>
      )}

      {loading.prsLoading && <Progress />}
      {!errors.prsError &&
        !loading.prsLoading &&
        (pullRequests?.length ?? 0) >= 1 && (
          <DevHubGrid container spacing={2}>
            {pullRequests
              .slice(0, 6 - incidentReport.incidents.length)
              .map((item, idx) => (
                <DevHubGrid item xs={12} lg={4}>
                  <WorkBoxItem
                    key={idx}
                    chipLabel={item.status}
                    chipColor={prStatusColors.get(item.status) || 'blue'}
                    title="Pull Request"
                    link={item.html_url}
                    body={item.title}
                    date={item.created_at}
                  />
                </DevHubGrid>
              ))}
          </DevHubGrid>
        )}
      <ConfigFeatureCheck feature={Feature.SECURITY_VULNERABILITIES}>
        <SecurityVulnerabilities
          expanded={secVulnsShowMore}
          onDataLoaded={hasData => setSecVulnsShowMore(hasData)}
        />
      </ConfigFeatureCheck>
    </DevHubBox>
  );
};

/**
 * A component that displays a summary of incidents and other notable activities for authenticated user.
 * @param props - The properties for the component.
 * @constructor
 */
export const MySummary = ({ expanded = false }: { expanded?: boolean }) => {
  const {
    value: gitHubPrsValue,
    loading: gitHubPrsIsLoading,
    error: gitHubPrsHasError,
  } = useGitHubPrRequest();

  const {
    value: incidentsValue,
    loading: incidentsIsLoading,
    error: incidentsHasError,
  } = useIOPIncidentRequest();

  return (
    <SummaryTable
      expanded={expanded}
      incidentOverview={incidentsValue as IncidentOverview}
      pullRequests={gitHubPrsValue as GitHubPr[]}
      errors={{
        incidentsHasError: Boolean(incidentsHasError),
        prsError: Boolean(gitHubPrsHasError),
      }}
      loading={{
        incidentsIsLoading,
        prsLoading: gitHubPrsIsLoading,
      }}
    />
  );
};
